import React from "react"
import _ from "lodash"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
    Button,
    IconButton,
    Tooltip,
    Chip,
    TextField,
    MenuItem,
    Tabs,
    Tab
} from "@mui/material"
import {
    Add,
    EditOutlined,
    DeleteOutline,
    VideoLibraryOutlined,
    SchoolOutlined,
    ChevronRight,
    GroupOutlined,
    Star,
    StarOutline,
    ForumOutlined,
    FolderOutlined,
    VisibilityOutlined
} from "@mui/icons-material"
import { Loader, ToastMessage, ConfirmationAlert } from "../../components/index"
import {
    useGetCoursesQuery,
    useCreateCourseMutation,
    useEditCourseMutation,
    useDeleteCourseMutation
} from "../../Redux/actions/lecture.action"
import { isOwner } from "../../utils/hooks"
import { formatPrice } from "../components/price"
import CourseFormDialog from "./CourseFormDialog"
import CourseReviewsDialog from "./CourseReviewsDialog"
import QuestionInbox from "./QuestionInbox"
import EmptyState from "../components/EmptyState"
import { managePaths } from "../constants"

/**
 * Owner screen for the platform's course catalogue. A course is created here,
 * then opened in the course builder (CourseBuilder) to add its chapters and
 * each chapter's videos. Courses belong to the platform, not to an institution: every student,
 * from any school, can buy what is published here.
 *
 * A second tab holds the Q&A inbox, where the owner answers the questions
 * students ask under lectures.
 */
export default function ManageCourses() {
    /* Courses are platform content, so only the owner manages them. */
    const canManage = isOwner()

    const coursesQuery = useGetCoursesQuery(undefined, { skip: !canManage })

    const [createCourse, createCourseState] = useCreateCourseMutation()
    const [editCourse, editCourseState] = useEditCourseMutation()
    const [deleteCourse, deleteCourseState] = useDeleteCourseMutation()

    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    /* The builder's Q&A button links here with ?tab=qa&course=<id> */
    const tab = searchParams.get("tab") === "qa" ? "qa" : "courses"
    const inboxCourseId = searchParams.get("course")
        ? Number(searchParams.get("course"))
        : null
    const setTab = (value) =>
        setSearchParams(value === "qa" ? { tab: "qa" } : {}, { replace: true })
    const setInboxCourseId = (value) =>
        setSearchParams(
            value ? { tab: "qa", course: String(value) } : { tab: "qa" },
            { replace: true }
        )

    const [classFilter, setClassFilter] = React.useState("all")

    const [courseDialog, setCourseDialog] = React.useState(null) // {course|null}
    const [reviewsCourse, setReviewsCourse] = React.useState(null)
    const [pendingDelete, setPendingDelete] = React.useState(null)

    const [notification, setNotification] = React.useState({
        show: false,
        type: "",
        message: ""
    })

    const notify = (type, message) =>
        setNotification({ show: true, type, message })

    const allCourses = React.useMemo(
        () => _.get(coursesQuery, "data.list", []),
        [coursesQuery]
    )

    /* The class is a free-text label on each course, so filter by it. */
    const classOptions = React.useMemo(
        () => _.uniq(allCourses.map((item) => item.class_name).filter(Boolean)).sort(),
        [allCourses]
    )

    const courses = React.useMemo(() => {
        if (classFilter === "all") return allCourses
        return allCourses.filter((item) => item.class_name === classFilter)
    }, [allCourses, classFilter])

    const openBuilder = (course) => navigate(managePaths.course(course.course_id))

    const openInbox = (course) => setInboxCourseId(course ? course.course_id : null)

    /* ----------------------------- courses ----------------------------- */

    const submitCourse = async (values) => {
        const payload = values
        try {
            if (courseDialog?.course) {
                await editCourse({
                    course_id: courseDialog.course.course_id,
                    ...payload
                }).unwrap()
                notify("success", "Course updated.")
            } else {
                const result = await createCourse(payload).unwrap()
                setCourseDialog(null)
                /* Straight on to adding its chapters and videos */
                navigate(managePaths.course(result.course.course_id))
                return
            }
            setCourseDialog(null)
        } catch (error) {
            notify(
                "error",
                _.get(error, "data.detail", null) || "Could not save the course."
            )
        }
    }

    const confirmDelete = async () => {
        const { item } = pendingDelete
        try {
            await deleteCourse({ course_id: item.course_id }).unwrap()
            notify("success", "Course removed.")
        } catch (error) {
            notify(
                "error",
                _.get(error, "data.detail", null) || "Could not remove that."
            )
        } finally {
            setPendingDelete(null)
        }
    }

    const busy =
        createCourseState.isLoading ||
        editCourseState.isLoading ||
        deleteCourseState.isLoading

    return (
        <div className="min-h-screen bg-slate-50 px-4 py-6 sm:px-6 sm:py-8">
            <div className="mx-auto w-full max-w-6xl space-y-6">

                <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="text-xs font-semibold tracking-widest text-indigo-500">
                            COURSE CATALOGUE
                        </p>
                        <h1 className="mt-1 text-xl font-bold text-slate-800 sm:text-2xl">
                            Manage courses
                        </h1>
                        <p className="mt-1 text-sm text-slate-500">
                            Publish a course, set its price, then open it to
                            add chapters and each chapter's videos.
                        </p>
                    </div>

                    {canManage && tab === "courses" && (
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => setCourseDialog({ course: null })}
                        >
                            New course
                        </Button>
                    )}
                </header>

                {!canManage && (
                    <EmptyState
                        icon={<SchoolOutlined />}
                        title="Owner only"
                        description="Courses are published by the platform owner and are available to students of every school."
                    />
                )}

                {canManage && (
                    <Tabs
                        value={tab}
                        onChange={(event, value) => setTab(value)}
                        variant="scrollable"
                        allowScrollButtonsMobile
                        sx={{ borderBottom: 1, borderColor: "divider" }}
                    >
                        <Tab value="courses" label="Courses" />
                        <Tab
                            value="qa"
                            label="Q&A inbox"
                            icon={<ForumOutlined fontSize="small" />}
                            iconPosition="start"
                            sx={{ minHeight: 48 }}
                        />
                    </Tabs>
                )}

                {canManage && tab === "qa" && (
                    <QuestionInbox
                        courses={allCourses}
                        courseId={inboxCourseId}
                        onCourseChange={setInboxCourseId}
                    />
                )}

                {/* Filter */}
                {canManage && tab === "courses" && (
                    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                        <TextField
                            select
                            size="small"
                            label="Class"
                            value={classFilter}
                            onChange={(event) =>
                                setClassFilter(event.target.value)
                            }
                            sx={{ minWidth: { xs: "100%", sm: 240 } }}
                        >
                            <MenuItem value="all">All classes</MenuItem>
                            {classOptions.map((item) => (
                                <MenuItem key={item} value={item}>
                                    {item}
                                </MenuItem>
                            ))}
                        </TextField>
                    </section>
                )}

                {canManage &&
                    tab === "courses" &&
                    !coursesQuery.isFetching &&
                    courses.length === 0 && (
                    <EmptyState
                        icon={<SchoolOutlined />}
                        title="No courses yet"
                        description="Create your first course — students of every school can buy it straight away."
                        actionLabel="New course"
                        onAction={() => setCourseDialog({ course: null })}
                    />
                )}

                {/* Course list */}
                {tab === "courses" && (
                    <section className="space-y-3">
                        {courses.map((course) => {
                            return (
                                <article
                                    key={course.course_id}
                                    className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
                                >
                                    {/* Course row */}
                                    <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                                        <div className="flex min-w-0 flex-1 items-start gap-3">
                                            <span
                                                className="mt-0.5 h-10 w-10 shrink-0 rounded-xl"
                                                style={{
                                                    background: `linear-gradient(135deg, ${
                                                        course.accent || "#4f46e5"
                                                    }, #0f172a)`
                                                }}
                                            />
                                            <div className="min-w-0">
                                                <h2 className="truncate text-sm font-semibold text-slate-800 sm:text-base">
                                                    <button
                                                        type="button"
                                                        onClick={() => openBuilder(course)}
                                                        className="max-w-full truncate text-left hover:text-indigo-600"
                                                    >
                                                        {course.title}
                                                    </button>
                                                </h2>
                                                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                    {course.class_name && (
                                                        <Chip
                                                            size="small"
                                                            label={course.class_name}
                                                        />
                                                    )}
                                                    <span>
                                                        {course.subject_name}
                                                    </span>
                                                    <span className="font-semibold text-slate-700">
                                                        {formatPrice(
                                                            course.price,
                                                            course.currency
                                                        )}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <VideoLibraryOutlined
                                                            sx={{ fontSize: 14 }}
                                                        />
                                                        {course.lecture_count || 0}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <GroupOutlined
                                                            sx={{ fontSize: 14 }}
                                                        />
                                                        {course.enrolled_count || 0}{" "}
                                                        enrolled
                                                    </span>
                                                    {course.chapter_count > 0 && (
                                                        <span className="flex items-center gap-1">
                                                            <FolderOutlined
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                            {course.chapter_count}
                                                        </span>
                                                    )}
                                                    {course.preview_count > 0 && (
                                                        <span className="flex items-center gap-1">
                                                            <VisibilityOutlined
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                            {course.preview_count}{" "}
                                                            preview
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1">
                                                        {course.rating_count > 0 ? (
                                                            <>
                                                                <Star
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        color: "#f59e0b"
                                                                    }}
                                                                />
                                                                {Number(
                                                                    course.rating_average || 0
                                                                ).toFixed(1)}{" "}
                                                                ({course.rating_count})
                                                            </>
                                                        ) : (
                                                            <>
                                                                <StarOutline
                                                                    sx={{ fontSize: 14 }}
                                                                />
                                                                No ratings
                                                            </>
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 self-end sm:self-center">
                                            {canManage && (
                                                <Tooltip title="Reviews">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setReviewsCourse(course)
                                                        }
                                                    >
                                                        <StarOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {canManage && (
                                                <Tooltip title="Questions from students">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openInbox(course)}
                                                    >
                                                        <ForumOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                endIcon={<ChevronRight />}
                                                onClick={() => openBuilder(course)}
                                                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                                            >
                                                Chapters & videos
                                            </Button>

                                            {canManage && (
                                                <Tooltip title="Edit course">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setCourseDialog({
                                                                course
                                                            })
                                                        }
                                                    >
                                                        <EditOutlined fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {canManage && (
                                                <Tooltip title="Remove course">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() =>
                                                            setPendingDelete({
                                                                kind: "course",
                                                                item: course
                                                            })
                                                        }
                                                    >
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>

                                </article>
                            )
                        })}
                    </section>
                )}
            </div>

            <CourseFormDialog
                open={Boolean(courseDialog)}
                course={courseDialog?.course}
                saving={createCourseState.isLoading || editCourseState.isLoading}
                onClose={() => setCourseDialog(null)}
                onSubmit={submitCourse}
            />

            <CourseReviewsDialog
                open={Boolean(reviewsCourse)}
                course={reviewsCourse}
                onClose={() => setReviewsCourse(null)}
            />

            {pendingDelete && (
                <ConfirmationAlert
                    open={true}
                    title="Remove this course?"
                    message={`"${pendingDelete.item.title}" and all of its chapters and lectures will be removed, and enrolled students will lose access.`}
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                />
            )}

            {(coursesQuery.isFetching || busy) && <Loader show={true} />}

            {notification.show && (
                <ToastMessage
                    {...notification}
                    onClose={() =>
                        setNotification({ show: false, type: "", message: "" })
                    }
                />
            )}
        </div>
    )
}
